import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { HashLoader } from 'react-spinners';
import './BlogPost.scss';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [navigation, setNavigation] = useState({ prev: null, next: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const decodeHtmlEntities = (value = '') => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');

    return doc.body.textContent || '';
  };

  const cleanExcerpt = (excerpt = '') => {
    return decodeHtmlEntities(excerpt)
      .replace(/\[…\]/g, '...')
      .replace(/…/g, '...')
      .trim();
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Pobierz aktualny post
        const postResponse = await axios.get(
          `https://marketingv8.scharmach.pl/wp-json/wp/v2/posts?slug=${slug}&_embed`
        );

        if (postResponse.data.length > 0) {
          const currentPost = postResponse.data[0];
          setPost(currentPost);

          // Pobierz wszystkie posty do nawigacji
          const allPostsResponse = await axios.get(
            'https://marketingv8.scharmach.pl/wp-json/wp/v2/posts?per_page=100&orderby=date&order=desc'
          );

          const allPosts = allPostsResponse.data;

          // Znajdź indeks aktualnego posta
          const currentIndex = allPosts.findIndex(
            p => p.id === currentPost.id
          );

          // Ustaw nawigację
          setNavigation({
            prev: currentIndex > 0
              ? allPosts[currentIndex - 1]
              : null,

            next: currentIndex < allPosts.length - 1
              ? allPosts[currentIndex + 1]
              : null
          });
        } else {
          setError('Post cannot be found');
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Error fetching post');
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className='loader-wrapper'>
        <HashLoader color="#fff" size={60} />
        <br />
        <p>Loading post...</p>
      </div>
    );
  }

  if (error) return <p>{error}</p>;
  if (!post) return <p>Post cannot be found</p>;

  const title = decodeHtmlEntities(post.title.rendered);
  const excerpt = cleanExcerpt(post.excerpt.rendered);

  const image =
    post._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
    'https://marketingv8.co/logov8.png';

  const prevTitle = navigation.prev
    ? decodeHtmlEntities(navigation.prev.title.rendered)
    : '';

  const nextTitle = navigation.next
    ? decodeHtmlEntities(navigation.next.title.rendered)
    : '';

  return (
    <article className='blog-post'>
      <Helmet>

        <link
          rel="canonical"
          href={`https://marketingv8.co/blog/${post.slug}`}
        />

        <title>
          {title} - Marketing v8
        </title>

        <meta
          name="description"
          content={excerpt}
        />

        {/* Open Graph */}
        <meta
          property="og:type"
          content="article"
        />

        <meta
          property="og:url"
          content={`https://marketingv8.co/blog/${post.slug}`}
        />

        <meta
          property="og:title"
          content={`${title} - Marketing v8`}
        />

        <meta
          property="og:description"
          content={excerpt}
        />

        <meta
          property="og:image"
          content={image}
        />

        <meta
          property="article:published_time"
          content={post.date}
        />

        <meta
          property="article:modified_time"
          content={post.modified}
        />

        {/* Twitter Card */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content={`${title} - Marketing v8`}
        />

        <meta
          name="twitter:description"
          content={excerpt}
        />

        <meta
          name="twitter:image"
          content={image}
        />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": title,
            "description": excerpt,
            "image": image,
            "url": `https://marketingv8.co/blog/${post.slug}`,
            "datePublished": post.date,
            "dateModified": post.modified,
            "publisher": {
              "@type": "Organization",
              "name": "Marketing v8",
              "url": "https://marketingv8.co",
              "logo": {
                "@type": "ImageObject",
                "url": "https://marketingv8.co/logov8.png"
              }
            }
          })}
        </script>

      </Helmet>

      <div className='blog-post-content'>

        {post._embedded?.['wp:featuredmedia'] && (
          <img
            src={image}
            alt={title}
            className='featured-image'
          />
        )}

        <h1 className='post-title'>
          {title}
        </h1>

        <p className='post-date'>
          {new Date(post.date).toLocaleDateString(
            'en-US',
            {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }
          )}
        </p>

        <div
          dangerouslySetInnerHTML={{
            __html: post.content.rendered
          }}
          className='post-content'
        />

        <nav className='post-navigation'>

          <div className='nav-links'>

            {navigation.prev && (
              <Link
                to={`/blog/${navigation.prev.slug}`}
                className='nav-link prev-post'
              >
                <span className='nav-label'>
                  ← Prev article
                </span>

                <span className='nav-title'>
                  {prevTitle}
                </span>
              </Link>
            )}

            {navigation.next && (
              <Link
                to={`/blog/${navigation.next.slug}`}
                className='nav-link next-post'
              >
                <span className='nav-label'>
                  Next article →
                </span>

                <span className='nav-title'>
                  {nextTitle}
                </span>
              </Link>
            )}

          </div>

        </nav>

      </div>

    </article>
  );
};

export default BlogPost;